import { getValueAtPath } from "@companies/shared/utils/get-value-at-path";
import { groupBy as groupByUtils } from "@companies/shared/utils/group-by";
import {
  faCaretDown,
  faCaretUp,
  faChevronDown,
  faChevronRight,
  faCog,
  faEdit,
  faEye,
  faFilter,
  faSort,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo, useRef, useState } from "react";

import { compareValues, Operator } from "../utils/compare-values";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Dropdown } from "./dropdown";
import { Input } from "./input";
import { Popover, PopoverRef } from "./popover";

export type Action<T> = {
  label: string | React.ReactNode;
  onClick?: (row: T, index: number) => void;
  route?: string;
};

export type Filter = {
  field: string;
  value?: unknown;
  fromValue?: unknown;
  toValue?: unknown;
  operator: Operator;
};

type FilterableBy = "text" | "select" | "date";
type FilterOption = { label: React.ReactNode; value: unknown };

type Locale = { yes: string; no: string; actions: string };

type SortCallback<T> = (
  a: T,
  b: T,
  field: string,
  direction: "asc" | "desc",
) => number;

export function Column<T>(_props: {
  field?: string;
  label?: string;
  header?: () => string | React.ReactNode;
  body?: (row: T, index: number) => string | React.ReactNode;
  headerClassName?: string;
  bodyClassName?: string;
  sortable?: boolean;
  filterableBy?: FilterableBy;
  hideFilter?: boolean;
  alwaysShow?: boolean;
  filterOptions?: (data: T[]) => FilterOption[];
  sortCallback?: SortCallback<T>;
  children?: React.ReactNode;
}) {
  return null;
}

export function PrefixRow({
  children,
  tdClassName,
}: {
  children: React.ReactNode;
  className?: string;
  tdClassName?: string;
}) {
  const columns = React.Children.toArray(children).filter(
    (child: unknown) =>
      typeof child === "object" &&
      child !== null &&
      "type" in child &&
      typeof child.type === "string" &&
      child.type === "td",
  ) as React.ReactElement<React.ComponentProps<"td">>[];
  return columns.map((column, index) => (
    <td
      key={index}
      {...column.props}
      className={`${tdClassName} ${column.props.className ?? ""}`}
    >
      {column.props.children}
    </td>
  ));
}

export function SuffixRow({
  children,
  tdClassName,
}: {
  children: React.ReactNode;
  className?: string;
  tdClassName?: string;
}) {
  const columns = React.Children.toArray(children).filter(
    (child: unknown) =>
      typeof child === "object" &&
      child !== null &&
      "type" in child &&
      typeof child.type === "string" &&
      child.type === "td",
  ) as React.ReactElement<React.ComponentProps<"td">>[];
  return columns.map((column, index) => (
    <td
      key={index}
      {...column.props}
      className={`${tdClassName} ${column.props.className ?? ""}`}
    >
      {column.props.children}
    </td>
  ));
}

export function Table<T>({
  data,
  subListBy,
  children,
  sortable = true,
  sortBy,
  sortByDirection = "asc",
  groupBy,
  onSort,
  filterBy = [],
  onFilter,
  showFieldSelector = false,
  onFieldSelectorChange,
  selectedColumns,
  actions,
  viewRouteButton,
  editRouteButton,
  removeRouteButton,
  locale,
}: {
  data: T[];
  subListBy?: string;
  children: React.ReactNode;
  sortable?: boolean;
  sortBy?: string;
  sortByDirection?: "asc" | "desc";
  groupBy?: string;
  onSort?: (field: string, direction: "asc" | "desc") => void;
  filterBy?: Filter[];
  onFilter?: (value: Filter[]) => void;
  showFieldSelector?: boolean;
  onFieldSelectorChange?: (value: unknown[]) => void;
  selectedColumns?: string[];
  actions?: (row: T, index: number) => Action<T>[];
  viewRouteButton?: (row: T, index: number) => string;
  editRouteButton?: (row: T, index: number) => string;
  removeRouteButton?: (row: T, index: number) => string;
  locale?: Locale;
}) {
  const columns = React.Children.toArray(children).filter(
    (child: unknown) =>
      typeof child === "object" &&
      child !== null &&
      "type" in child &&
      typeof child.type === "function" &&
      child.type.name === Column.name,
  ) as React.ReactElement<Parameters<typeof Column>[0]>[];

  const prefixRow = React.Children.toArray(children).filter(
    (child: unknown) =>
      typeof child === "object" &&
      child !== null &&
      "type" in child &&
      typeof child.type === "function" &&
      child.type.name === PrefixRow.name,
  )[0] as React.ReactElement<Parameters<typeof PrefixRow>[0]>;

  const suffixRow = React.Children.toArray(children).filter(
    (child: unknown) =>
      typeof child === "object" &&
      child !== null &&
      "type" in child &&
      typeof child.type === "function" &&
      child.type.name === SuffixRow.name,
  )[0] as React.ReactElement<Parameters<typeof SuffixRow>[0]>;

  const [sortField, setSortField] = useState<string>(sortBy ?? "");
  const sortColumnCallback = useRef<SortCallback<T> | undefined>(undefined);

  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    sortByDirection ?? "asc",
  );

  const sortedData = useMemo(() => {
    const a = [...data].sort((a, b) => {
      const fieldValueA = getValueAtPath(a, sortField);
      const fieldValueB = getValueAtPath(b, sortField);

      if (sortColumnCallback.current) {
        return sortColumnCallback.current(a, b, sortField, sortDirection);
      }

      let result = 0;
      if (typeof fieldValueA === "string" && typeof fieldValueB === "string") {
        result = fieldValueA.localeCompare(fieldValueB);
      } else if (
        typeof fieldValueA === "number" &&
        typeof fieldValueB === "number"
      ) {
        result = fieldValueA - fieldValueB;
      } else if (fieldValueA instanceof Date && fieldValueB instanceof Date) {
        result = fieldValueA.getTime() - fieldValueB.getTime();
      } else {
        // Last resort comparing values as numbers. This might fail if values are not comparable
        result = (fieldValueA as number) - (fieldValueB as number);
      }

      if (fieldValueA === undefined) {
        return -1;
      }

      if (fieldValueB === undefined) {
        return 1;
      }

      if (sortDirection === "asc") {
        return result;
      }
      return -result;
    });

    if (groupBy) {
      return groupByUtils(a, groupBy as keyof T);
    } else {
      return { all: a };
    }
  }, [data, sortField, sortDirection, sortColumnCallback, groupBy]);

  const cellPadding = "py-2 px-4";
  const cellAlign = "text-left";

  const headerClassName = `${cellPadding} ${cellAlign} font-normal bg-gray-100 border-b-1 border-gray-300 whitespace-nowrap`;
  const cellClassName = `${cellPadding} ${cellAlign} text-gray-500 border-b-1 border-gray-300`;

  return (
    <div className="w-full flex flex-col gap-2">
      {showFieldSelector && (
        <Dropdown
          asCheckbox
          showSelectAll
          buttonSeverity="secondary"
          buttonSize="sm"
          itemClassName="hover:bg-gray-400"
          label={<FontAwesomeIcon icon={faFilter} />}
          listClassName="bg-gray-200 whitespace-nowrap rounded-sm border-1 border-black"
          options={columns.map((column) => ({
            label:
              column.props.children ?? column.props.label ?? column.props.field,
            value: column.props.field,
            checked:
              selectedColumns?.some((c) => {
                return c === column.props.field;
              }) || column.props.alwaysShow,
            disabled: column.props.alwaysShow,
          }))}
          onSelect={(value) => {
            onFieldSelectorChange?.(
              toggleValueFromArray(selectedColumns ?? [], value),
            );
          }}
          onSelectAll={() => {
            if (
              selectedColumns?.length ===
              columns.filter((column) => !column.props.alwaysShow).length
            ) {
              onFieldSelectorChange?.([]);
            } else {
              onFieldSelectorChange?.(
                columns
                  .filter(
                    (column) => column.props.field && !column.props.alwaysShow,
                  )
                  .map((column) => column.props.field ?? ""),
              );
            }
          }}
        />
      )}
      <table className="w-full">
        <thead>
          <tr>
            {subListBy && <th className={`${headerClassName} text-left w-1`} />}
            {columns
              .filter(
                (column) =>
                  !showFieldSelector ||
                  column.props.alwaysShow ||
                  column.props.field === undefined ||
                  selectedColumns === undefined ||
                  selectedColumns?.includes(column.props.field),
              )
              .map((column, index) => (
                <TableHeader
                  key={column.props.field ?? `header-${index}`}
                  className={`${headerClassName} ${column.props.headerClassName ?? ""}`}
                  field={column.props.field}
                  filterableBy={column.props.filterableBy}
                  filterBy={filterBy}
                  filterOptions={column.props.filterOptions?.(data)}
                  header={column.props.header}
                  hideFilter={column.props.hideFilter}
                  sortable={sortable ? column.props.sortable : false}
                  sortDirection={sortByDirection}
                  sortField={sortBy}
                  onFilter={onFilter}
                  onSort={(field: string, direction: "asc" | "desc") => {
                    onSort?.(field, direction);
                    sortColumnCallback.current = column.props.sortCallback;
                    setSortField(field);
                    setSortDirection(direction);
                  }}
                >
                  {column.props.children}
                </TableHeader>
              ))}
            {(actions ||
              viewRouteButton ||
              editRouteButton ||
              removeRouteButton) && (
              <th className={`${headerClassName} text-right`}>
                {locale && locale.actions ? locale.actions : "ACTIONS"}
              </th>
            )}
          </tr>
        </thead>
        {prefixRow && (
          <tbody>
            <tr className={`${prefixRow.props.className ?? ""}`}>
              <PrefixRow
                tdClassName={`${cellClassName}`}
                {...prefixRow.props}
              />
            </tr>
          </tbody>
        )}
        {Object.entries(sortedData).map(([key, value]) => (
          <TableGroup<T>
            key={key}
            actions={actions}
            cellClassName={cellClassName}
            columns={columns}
            editRouteButton={editRouteButton}
            filterBy={filterBy}
            groupBy={groupBy}
            groupName={key}
            locale={locale}
            removeRouteButton={removeRouteButton}
            rows={value}
            selectedColumns={selectedColumns}
            showFieldSelector={showFieldSelector}
            subListBy={subListBy}
            viewRouteButton={viewRouteButton}
          />
        ))}
        {suffixRow && (
          <tbody>
            <tr className={`${suffixRow.props.className ?? ""}`}>
              <SuffixRow
                tdClassName={`${cellClassName}`}
                {...suffixRow.props}
              />
            </tr>
          </tbody>
        )}
      </table>
    </div>
  );
}

function TableHeader({
  field,
  header,
  className,
  children,
  sortable = true,
  filterableBy,
  hideFilter,
  filterOptions,
  onFilter,
  sortField,
  sortDirection,
  filterBy,
  onSort,
}: {
  field?: string;
  className?: string;
  header?: () => string | React.ReactNode;
  children?: React.ReactNode;
  sortable?: boolean;
  filterableBy?: FilterableBy;
  hideFilter?: boolean;
  filterOptions?: FilterOption[];
  onFilter?: (value: Filter[]) => void;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  filterBy?: Filter[];
  onSort: (field: string, direction: "asc" | "desc") => void;
}) {
  const isSortedByField = sortField === field;
  const isSortedAsc = isSortedByField && sortDirection === "asc";

  let sortIcon = faSort;
  if (isSortedByField) {
    sortIcon = isSortedAsc ? faCaretUp : faCaretDown;
  }

  return (
    <th key={field} className={className}>
      <div className="flex items-center gap-2">
        <button
          className={`flex items-center gap-2 ${sortable ? "cursor-pointer" : ""}`}
          onClick={() => {
            if (sortable && field) {
              onSort(field, isSortedAsc ? "desc" : "asc");
            }
          }}
        >
          {header?.() ?? children ?? field}
          {sortable && <FontAwesomeIcon icon={sortIcon} />}
        </button>
        {field && filterableBy && !hideFilter && (
          <ColumnFilter
            field={field}
            filterableBy={filterableBy}
            filterOptions={filterOptions}
            value={
              filterBy?.find((filter) => filter.field === field)?.value ?? []
            }
            onFilter={(filter: Filter) => {
              onFilter?.([
                // Remove already existing filters for this field
                ...(filterBy?.filter((filter) => filter.field !== field) ?? []),
                // Add new filter if it's not empty
                ...(!isFilterValueEmpty(filter) ? [filter] : []),
              ]);
            }}
          />
        )}
      </div>
    </th>
  );
}

function TableGroup<T>({
  rows,
  groupBy,
  filterBy,
  cellClassName,
  columns,
  groupName,
  actions,
  editRouteButton,
  locale,
  removeRouteButton,
  selectedColumns,
  showFieldSelector,
  subListBy,
  viewRouteButton,
}: {
  rows: T[];
  groupBy?: string;
  filterBy?: Filter[];
  cellClassName: string;
  columns: React.ReactElement<Parameters<typeof Column>[0]>[];
  groupName: string;
  actions?: (row: T, index: number) => Action<T>[];
  editRouteButton?: (row: T, index: number) => string;
  locale?: Locale;
  removeRouteButton?: (row: T, index: number) => string;
  selectedColumns?: string[];
  showFieldSelector?: boolean;
  subListBy?: string;
  viewRouteButton?: (row: T, index: number) => string;
}) {
  const [folded, setFolded] = useState(false);

  return (
    <>
      {groupBy && (
        <tr>
          <td
            className={`${cellClassName} font-bold`}
            colSpan={columns.length + 1}
          >
            <button onClick={() => setFolded(!folded)}>
              <FontAwesomeIcon icon={folded ? faChevronRight : faChevronDown} />
            </button>
            {groupName}
          </td>
        </tr>
      )}
      {!folded && (
        <tbody>
          {rows
            .filter((row) => filterData<T>(row, filterBy ?? []))
            .map((row, index) => (
              <TableRow
                key={`row-${index}`}
                actions={actions}
                cellClassName={cellClassName}
                columns={columns}
                editRouteButton={editRouteButton}
                index={index}
                locale={locale}
                removeRouteButton={removeRouteButton}
                row={row}
                selectedColumns={selectedColumns}
                showFieldSelector={showFieldSelector}
                subListBy={subListBy}
                viewRouteButton={viewRouteButton}
              />
            ))}
        </tbody>
      )}
    </>
  );
}

function TableRow<T>({
  index,
  columns,
  showFieldSelector,
  selectedColumns,
  cellClassName,
  row,
  actions,
  viewRouteButton,
  editRouteButton,
  removeRouteButton,
  locale,
  subListBy,
  isSubList = false,
}: {
  index: number;
  columns: React.ReactElement<Parameters<typeof Column>[0]>[];
  showFieldSelector?: boolean;
  selectedColumns?: string[];
  cellClassName: string;
  row: T;
  actions?: (row: T, index: number) => Action<T>[];
  viewRouteButton?: (row: T, index: number) => string;
  editRouteButton?: (row: T, index: number) => string;
  removeRouteButton?: (row: T, index: number) => string;
  locale?: Locale;
  subListBy?: string;
  isSubList?: boolean;
}) {
  const subList = subListBy
    ? (getValueAtPath(row, subListBy) as T[])
    : undefined;
  const subListCount = subList ? subList.length : 0;

  const isSubListView = subListBy || isSubList;

  const [isSubListExpanded, setIsSubListExpanded] = useState(false);

  const subListClassName = `${cellClassName} ${isSubList ? "bg-gray-300" : ""}`;
  return (
    <>
      <tr>
        {isSubListView && (
          <td className={`${subListClassName} text-left`}>
            {subListCount > 0 && (
              <Button
                size="sm"
                text={true}
                onClick={() => setIsSubListExpanded(!isSubListExpanded)}
              >
                <FontAwesomeIcon
                  icon={isSubListExpanded ? faChevronDown : faChevronRight}
                />
              </Button>
            )}
          </td>
        )}
        {columns
          .filter(
            (column) =>
              !showFieldSelector ||
              column.props.alwaysShow ||
              column.props.field === undefined ||
              selectedColumns === undefined ||
              selectedColumns.includes(column.props.field),
          )
          .map((column, columnIndex) => (
            <TableBody<T>
              key={column.props.field ?? `body-${columnIndex}`}
              body={column.props.body}
              className={`${subListClassName} ${column.props.bodyClassName ?? ""}`}
              data={row}
              field={column.props.field}
              index={index}
              locale={locale}
            />
          ))}
        {(actions ||
          viewRouteButton ||
          editRouteButton ||
          removeRouteButton) && (
          <td className={`${cellClassName} text-right`}>
            <div className="flex gap-2 justify-end">
              {actions && (
                <ActionsDropdown
                  actions={actions(row, index)}
                  index={index}
                  row={row}
                />
              )}
              {viewRouteButton && (
                <Button route={viewRouteButton(row, index)} size="sm">
                  <FontAwesomeIcon icon={faEye} />
                </Button>
              )}
              {editRouteButton && (
                <Button
                  route={editRouteButton(row, index)}
                  severity="secondary"
                  size="sm"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </Button>
              )}
              {removeRouteButton && (
                <Button
                  route={removeRouteButton(row, index)}
                  severity="danger"
                  size="sm"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              )}
            </div>
          </td>
        )}
      </tr>
      {subList &&
        isSubListExpanded &&
        subList.map((subRow, subIndex) => (
          <TableRow
            key={`subRow-${subIndex}`}
            actions={actions}
            cellClassName={cellClassName}
            columns={columns}
            editRouteButton={editRouteButton}
            index={subIndex}
            isSubList={true}
            locale={locale}
            removeRouteButton={removeRouteButton}
            row={subRow}
            selectedColumns={selectedColumns}
            showFieldSelector={showFieldSelector}
            viewRouteButton={viewRouteButton}
          />
        ))}
    </>
  );
}

function TableBody<T>({
  field,
  className,
  body,
  index,
  data,
  locale,
}: {
  field?: string;
  className?: string;
  body?: <T>(row: T, index: number) => string | React.ReactNode;
  index: number;
  data: T;
  locale?: Locale;
}) {
  return (
    <td className={className}>
      {body?.(data, index) ??
        (field &&
          (formatFieldValue(
            getValueAtPath(data, field),
            locale,
          ) as React.ReactNode))}
    </td>
  );
}

function ColumnFilter({
  field,
  filterableBy,
  filterOptions,
  value,
  fromValue,
  toValue,
  onFilter,
}: {
  field: string;
  filterableBy: FilterableBy;
  filterOptions?: FilterOption[];
  value?: unknown;
  fromValue?: string;
  toValue?: string;
  onFilter: (value: Filter) => void;
}) {
  const [inputValue, setInputValue] = useState<string>(value?.toString() ?? "");
  const [inputFromValue, setInputFromValue] = useState<string>(
    fromValue?.toString() ?? "",
  );
  const [inputToValue, setInputToValue] = useState<string>(
    toValue?.toString() ?? "",
  );

  const isFilterApplied =
    value && value instanceof Array ? value.length > 0 : value !== "";

  const popoverRef = useRef<PopoverRef>(null);

  return (
    <Popover
      ref={popoverRef}
      handler={
        <FontAwesomeIcon
          className={`border-b-2 relative top-[2px] ${isFilterApplied ? "border-black" : "border-transparent"}`}
          icon={faFilter}
        />
      }
      offset={{ x: 0, y: 22 }}
    >
      <div className="flex flex-col gap-2 bg-gray-200 p-2 min-w-[250px] border-1 border-gray-300">
        {filterableBy === "date" && (
          <form
            className="flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              onFilter({
                field,
                fromValue: inputFromValue,
                toValue: inputToValue,
                operator: "between",
              });
              popoverRef.current?.close();
            }}
          >
            <div className="flex items-center gap-2 justify-between">
              <span className="w-[50px]">From</span>
              <Input
                clearButton
                type="date"
                value={inputFromValue}
                onChange={(e) => setInputFromValue(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 justify-between">
              <span className="w-[50px]">To</span>
              <Input
                clearButton
                type="date"
                value={inputToValue}
                onChange={(e) => setInputToValue(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-between">
              <Button size="sm" type="submit">
                Apply
              </Button>
              <Button
                severity="danger"
                size="sm"
                type="button"
                onClick={() => {
                  setInputFromValue("");
                  setInputToValue("");
                  onFilter({
                    field,
                    fromValue: "",
                    toValue: "",
                    operator: "between",
                  });
                  popoverRef.current?.close();
                }}
              >
                Clear
              </Button>
            </div>
          </form>
        )}
        {filterableBy === "select" && filterOptions && (
          <ul className="flex flex-col gap-2">
            {filterOptions.map((option, index) => (
              <li key={index}>
                <Checkbox
                  checked={
                    value && value instanceof Array
                      ? value.includes(option.value)
                      : false
                  }
                  className="flex items-center gap-2"
                  label={option.label}
                  onClick={() => {
                    if (value && value instanceof Array) {
                      onFilter({
                        field,
                        value: toggleValueFromArray(
                          value && value instanceof Array ? value : [],
                          option.value,
                        ),
                        operator: "eq",
                      });
                    }
                  }}
                />
              </li>
            ))}
          </ul>
        )}{" "}
        {filterableBy === "text" && (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              onFilter({
                field,
                value: inputValue,
                operator: "contains",
              });
              popoverRef.current?.close();
            }}
          >
            <Input
              clearButton
              inputClassName="min-w-[150px]"
              size="sm"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <Button size="sm" type="submit">
              Apply
            </Button>
          </form>
        )}
      </div>
    </Popover>
  );
}

function ActionsDropdown<T>({
  actions,
  row,
  index,
}: {
  actions: Action<T>[];
  row: T;
  index: number;
}) {
  return (
    <Dropdown
      buttonSeverity="secondary"
      buttonSize="sm"
      disabled={actions.length === 0}
      itemClassName="hover:bg-gray-400"
      label={<FontAwesomeIcon icon={faCog} />}
      listClassName="bg-gray-200 whitespace-nowrap rounded-sm border-1 border-black"
      options={actions.map((action, index) => ({
        label: action.label,
        value: index.toString(),
        ...(action.route ? { route: action.route } : {}),
      }))}
      onSelect={(value) => {
        const action = actions[parseInt(value)];
        action?.onClick?.(row, index);
      }}
    />
  );
}

function formatFieldValue(value: unknown, locale?: Locale): unknown {
  if (value instanceof Date) {
    return value.toLocaleDateString();
  } else if (typeof value === "boolean") {
    if (locale) {
      return value ? locale.yes : locale.no;
    }
    return value ? "TRUE" : "FALSE";
  }
  return value;
}

function toggleValueFromArray(array: unknown[], value: unknown) {
  if (array.includes(value)) {
    return array.filter((v) => v !== value);
  }
  return [...array, value];
}

function isFilterValueEmpty(filter: Filter) {
  const value = filter.value;
  return (
    (value instanceof Array && value.length === 0) ||
    (value === "" && value !== undefined) ||
    (filter.fromValue === "" &&
      filter.fromValue !== undefined &&
      filter.toValue === "" &&
      filter.toValue !== undefined)
  );
}

function filterData<T>(row: T, filterBy: Filter[]) {
  return filterBy.every((filter) => {
    return compareValues(
      getValueAtPath(row, filter.field),
      filter.value,
      filter.operator,
    );
  });
}
